import React from 'react';
import Badge from '../ui/Badge';

const SOURCE_LABELS = {
  momo_topup:        'MoMo Top-Up',
  service_deduction: 'Service Charge',
  admin_deduction:   'Admin Deduction',
  refund:            'Refund',
};

const STATUS_COLOR = {
  pending:   'orange',
  completed: 'green',
  failed:    'red',
};

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Intl.DateTimeFormat('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: 'numeric', minute: '2-digit', hour12: true,
    }).format(new Date(iso));
  } catch {
    return iso;
  }
};

const TransactionList = ({ transactions = [], isLoading }) => {
  if (isLoading) {
    return (
      <div className="mx-5 mt-5 space-y-3">
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-16 rounded-2xl shimmer-bg" />
        ))}
      </div>
    );
  }

  if (transactions.length === 0) {
    return (
      <div className="mx-5 mt-5 rounded-3xl border border-surface-500 bg-surface-800 flex flex-col items-center justify-center py-12 gap-3 animate-fadeUp">
        <div
          className="w-14 h-14 rounded-2xl flex items-center justify-center"
          style={{ background: 'rgba(99,102,241,0.08)' }}
        >
          <span className="text-2xl">💳</span>
        </div>
        <p className="font-syne font-bold text-slate-700 text-sm">No transactions yet</p>
        <p className="font-dm text-surface-300 text-xs text-center max-w-[220px] leading-relaxed">
          Top up your wallet or submit a service request to see activity here.
        </p>
      </div>
    );
  }

  return (
    <div className="mx-5 mt-5 animate-fadeUp">
      <div className="space-y-2">
        {transactions.map((txn, idx) => {
          const isCredit = txn.type === 'credit';
          return (
            <div
              key={txn.transaction_id || idx}
              className="rounded-2xl border border-surface-500 bg-surface-800 p-4 card-shadow"
              style={{ animationDelay: `${idx * 0.04}s` }}
            >
              {/* Top row: source label + amount */}
              <div className="flex items-start justify-between gap-3 mb-2">
                <div className="flex items-center gap-2.5 min-w-0">
                  <div
                    className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 text-base"
                    style={{
                      background: isCredit ? 'rgba(16,185,129,0.10)' : 'rgba(244,63,94,0.10)',
                    }}
                  >
                    {isCredit ? '⬆️' : '⬇️'}
                  </div>
                  <div className="min-w-0">
                    <p className="font-dm font-semibold text-slate-800 text-sm leading-tight truncate">
                      {SOURCE_LABELS[txn.source] || txn.source}
                    </p>
                    <p className="font-dm text-surface-300 text-xs mt-0.5 leading-tight truncate">
                      {txn.description}
                    </p>
                  </div>
                </div>

                <div className="text-right flex-shrink-0">
                  <p
                    className="font-syne font-bold text-base leading-tight"
                    style={{ color: isCredit ? '#10b981' : '#f43f5e' }}
                  >
                    {isCredit ? '+' : '−'} GHS{' '}
                    {Number(txn.amount).toLocaleString('en-GH', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </p>
                  <p className="font-dm text-xs text-surface-300 mt-0.5">
                    Bal: GHS{' '}
                    {Number(txn.balance_after).toLocaleString('en-GH', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </p>
                </div>
              </div>

              {/* Bottom row: date + status */}
              <div className="flex items-center justify-between pt-2 border-t border-surface-500/50">
                <p className="font-dm text-xs text-surface-300">{formatDate(txn.created_at)}</p>
                <Badge
                  label={txn.status}
                  color={STATUS_COLOR[txn.status] || 'gray'}
                  dot
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default TransactionList;
